import React, { useCallback, useEffect, useState } from 'react';

import { listFeatureIdeas, saveFeatureIdea, type AdminFeatureIdea, type FeatureIdeaStatus } from './api';
import { onlyDate } from './money';
import { RELEASE_NOTE_OPTIONS } from './releaseNoteOptions';

/**
 * Ideas: the board people see under Help & support > Ideas we're considering. Every
 * idea is written here, in your words, never a customer's text. Only published ideas
 * show; a vote follows the idea, and voters are told when its status changes.
 */

const STATUS_LABEL: Record<FeatureIdeaStatus, string> = {
  considering: 'Being considered',
  planned: 'Planned',
  in_progress: 'Being built',
  shipped: 'In the app',
};

type Draft = {
  ideaId: string | null;
  title: string;
  summary: string;
  status: FeatureIdeaStatus;
  published: boolean;
  shippedRelease: string;
};

const EMPTY: Draft = { ideaId: null, title: '', summary: '', status: 'considering', published: false, shippedRelease: '' };

export default function FeatureIdeas({ toast }: { toast: (message: string) => void }) {
  const [rows, setRows] = useState<AdminFeatureIdea[] | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setRows(await listFeatureIdeas());
    } catch (e) {
      toast(e instanceof Error ? e.message : String(e));
      setRows([]);
    }
  }, [toast]);
  useEffect(() => void load(), [load]);

  const save = async () => {
    if (!draft) return;
    setBusy(true);
    try {
      await saveFeatureIdea({
        ideaId: draft.ideaId,
        title: draft.title,
        summary: draft.summary,
        status: draft.status,
        published: draft.published,
        shippedRelease: draft.status === 'shipped' ? draft.shippedRelease || null : null,
      });
      toast(draft.published ? 'Saved. Voters hear about a status change.' : 'Saved as a draft.');
      setDraft(null);
      await load();
    } catch (e) {
      toast(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  if (rows === null) return <p className="muted">Loading ideas…</p>;

  return (
    <>
      <div className="row" style={{ marginBottom: 12 }}>
        <button className="btn primary" onClick={() => setDraft({ ...EMPTY })}>New idea</button>
      </div>

      {draft ? (
        <div className="card" style={{ marginBottom: 16 }}>
          <h2 style={{ margin: 0 }}>{draft.ideaId ? 'Edit idea' : 'New idea'}</h2>
          <label>Title (what people see)</label>
          <input value={draft.title} maxLength={80} onChange={(e) => setDraft({ ...draft, title: e.target.value })} />
          <label>Summary</label>
          <textarea className="field" value={draft.summary} maxLength={600} onChange={(e) => setDraft({ ...draft, summary: e.target.value })} />
          <label>Status</label>
          <select className="field" style={{ maxWidth: 260 }} value={draft.status} onChange={(e) => setDraft({ ...draft, status: e.target.value as FeatureIdeaStatus })}>
            {(Object.keys(STATUS_LABEL) as FeatureIdeaStatus[]).map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
          </select>
          {draft.status === 'shipped' ? (
            <>
              <label>Shipped in</label>
              <select className="field" style={{ maxWidth: 420 }} value={draft.shippedRelease} onChange={(e) => setDraft({ ...draft, shippedRelease: e.target.value })}>
                <option value="">No note</option>
                {RELEASE_NOTE_OPTIONS.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
              </select>
            </>
          ) : null}
          <label className="checkline">
            <input type="checkbox" checked={draft.published} onChange={(e) => setDraft({ ...draft, published: e.target.checked })} />
            Show on the ideas board in the app
          </label>
          <div className="row" style={{ marginTop: 14, gap: 8 }}>
            <button className="btn primary" disabled={busy || !draft.title.trim()} onClick={() => void save()}>Save</button>
            <button className="btn" onClick={() => setDraft(null)}>Cancel</button>
          </div>
        </div>
      ) : null}

      {rows.length === 0 ? (
        <p className="muted">No ideas yet. Start one here, or from a request under Feature requests.</p>
      ) : (
        <div className="matrix-scroll">
          <table className="matrix">
            <thead>
              <tr><th>Idea</th><th>Status</th><th>On the board</th><th>Votes</th><th>Requests</th><th>Updated</th><th /></tr>
            </thead>
            <tbody>
              {rows.map((i) => (
                <tr key={i.id}>
                  <td style={{ whiteSpace: 'normal', maxWidth: 420 }}>
                    <strong>{i.title}</strong>
                    {i.summary ? <div className="muted" style={{ fontSize: 12 }}>{i.summary}</div> : null}
                  </td>
                  <td>{STATUS_LABEL[i.status] ?? i.status}</td>
                  <td>{i.published ? 'Yes' : <span className="muted">Draft</span>}</td>
                  <td>{i.vote_count}</td>
                  <td>{i.request_count}</td>
                  <td>{onlyDate(i.updated_at)}</td>
                  <td style={{ textAlign: 'right' }}>
                    <button
                      className="btn"
                      onClick={() =>
                        setDraft({
                          ideaId: i.id,
                          title: i.title,
                          summary: i.summary,
                          status: i.status,
                          published: i.published,
                          shippedRelease: i.shipped_release ?? '',
                        })
                      }
                    >
                      Edit
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
